/**
 * Pathless layout for authenticated routes. TanStack Router collects
 * every file under `routes/_authenticated/` and wraps them in this
 * component. The guard kicks unauthenticated users to /login.
 *
 * Two redirect mechanisms, by design (mirrors /login's two-mechanism
 * guard, inverted):
 *
 *   1. `beforeLoad` (synchronous, one-shot at navigation time): if the
 *      store ALREADY says `unauthenticated` when we navigate in, bounce
 *      to /login before the shell ever paints. It deliberately fires
 *      ONLY on `unauthenticated` — never on `idle`/`loading`, so a
 *      valid-but-still-bootstrapping user is never bounced.
 *
 *   2. A reactive store subscription (auth-redirect fix, 2026-06-14):
 *      `status` is NOT persisted (auth-store partialize), so after a
 *      reload it starts `idle`, AuthBootstrap flips it to `loading`,
 *      then GET /api/auth/me resolves it to `authenticated` (200) or
 *      `unauthenticated` (401). `beforeLoad` already ran by then and
 *      does NOT re-run, so a session that turns out dead would otherwise
 *      leave the user staring at a board shell whose every query 401s.
 *      We subscribe to the store and, the moment it transitions INTO
 *      `unauthenticated`, send the user to /login.
 *
 * The reactive redirect lives at module scope (not in a component) for
 * the same reason /login's does: there is no exported router singleton
 * to call `navigate` on from module scope, and a full-document
 * navigation is what the rest of the app already uses for cross-context
 * routing (see the sidebar logout handlers).
 *
 * SAFETY (the #1 risk is logging out a VALID session):
 *   - The subscription fires ONLY on the `* -> unauthenticated`
 *     transition. `idle` and `loading` never trigger it.
 *   - While `status` is `idle`/`loading`/`unauthenticated`, the
 *     component renders a lightweight splash instead of the board, so a
 *     bootstrapping user sees a loader (not a dead shell) and a
 *     confirmed-logged-out user sees a loader for the instant before the
 *     redirect lands (never the dead board).
 *   - We never redirect when the browser is already standing on a PUBLIC
 *     page (no loop, and no eviction of a person who has no account yet).
 *     The set of public pages is `src/lib/public-paths.ts`; until
 *     2026-08-05 it was the inline literal '/login', which is why an
 *     invitation link bounced its recipient to a password prompt.
 */
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

import { Shell } from '../components/layout/shell';
import { PrivatePortalBoundary } from '../components/ui/private-portal';
import { isPublicPath } from '../lib/public-paths';
import { useVerifiedPrincipal } from '../lib/verified-principal';
import { useAuthStore } from '../stores/auth-store';

// Guarded, one-way reactive redirect: only when the store transitions
// INTO `unauthenticated` AND the browser is NOT already on /login.
// Never fires on `idle`/`loading` — a valid-but-bootstrapping session is
// safe.
useAuthStore.subscribe((state, prev) => {
  if (state.status !== 'unauthenticated' || prev.status === 'unauthenticated') return;
  if (typeof window === 'undefined') return;
  // EVERY public page, not just /login (P0, 2026-08-05). This subscription
  // is installed at module scope in a non-lazy half of the route tree, so it
  // runs on pages that are not under `_authenticated` at all. An invited
  // person on /signup?token=… has no account by definition: /me answers 401,
  // the store flips to `unauthenticated`, and the single-string exemption
  // threw them onto a sign-in form they have no password for — while their
  // invitation had already loaded. See src/lib/public-paths.ts.
  if (isPublicPath(window.location.pathname)) return;
  window.location.assign('/login');
});

/** Minimal centered splash shown while auth is still resolving. */
function AuthSplash() {
  return (
    <div
      className="flex h-screen w-full items-center justify-center bg-background"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="sr-only">Loading</span>
      <span
        className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        aria-hidden
      />
    </div>
  );
}

function AuthenticatedLayout() {
  const status = useAuthStore((s) => s.status);
  const principal = useVerifiedPrincipal();
  const isVerified =
    principal.status === 'verified'
    && principal.userId !== null
    && principal.principalFence !== null;
  const canRetainVeiledSubtree =
    status === 'authenticated'
    && principal.status === 'verifying'
    && principal.userId !== null
    && principal.principalFence !== null;

  // Initial bootstrap, explicit principal transitions, BFCache pagehide and
  // failures still unmount the private tree. A routine same-lifetime freshness
  // check retains its owner component so an in-flight Send does not lose the
  // mounted action token, but the tree is hidden + inert until /me succeeds.
  if (status !== 'authenticated' || (!isVerified && !canRetainVeiledSubtree)) {
    return <AuthSplash />;
  }

  const veiled = !isVerified;
  return (
    <>
      <PrivatePortalBoundary veiled={veiled}>
        <Shell key={principal.generation}>
          <Outlet />
        </Shell>
      </PrivatePortalBoundary>
      {veiled ? <AuthSplash /> : null}
    </>
  );
}

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: () => {
    const state = useAuthStore.getState();
    // One-shot: only redirect a session already CONFIRMED unauthenticated
    // at navigation time. `idle`/`loading` fall through to the splash.
    if (state.status === 'unauthenticated') {
      throw redirect({ to: '/login' });
    }
  },
  component: AuthenticatedLayout,
});
